import numeral from 'numeral'
import { MILES_KM_CONVERSION_NUMBER } from 'app/settings/defaultValues'
import {
  EMAIL_FORMAT,
  ERROR_EMAIL_INVALID_FORMAT,
  ERROR_EMAIL_NOT_JUST_WHITESPACE,
  ERROR_PASSWORD_MAX_LENGTH,
  ERROR_PASSWORD_MIN_LENGTH,
  ERROR_PASSWORD_NOT_JUST_WHITESPACE,
  PASSWORD_MAX_LENGTH,
  PASSWORD_MIN_LENGTH
} from 'pages/account/profile/validate'
import { GetTransactionsParams } from 'types/transactions'
import { MotusPurpose } from 'types/enums'
import { GetTripsParams } from 'types/trips'
import { PaymentScheduleConfiguration } from 'types/schedules'
import { ErrorMessage } from 'types/utility'

interface PurposeColor {
  class: string
  hex: string
}

interface PurposeColors {
  default: PurposeColor
  other: PurposeColor
  personal: PurposeColor
  unclassified: PurposeColor
}

interface StatusColor {
  color: string
  bgColor: string
}

interface PayPeriodStatusColors {
  [key: string]: StatusColor
}

interface ComplianceStatusColors {
  [key: string]: StatusColor
}

/**
 * TODO & Note from Mariano: according to https://developer.mozilla.org/en-US/docs/Web/JavaScript/Reference/Global_Objects/Math/round
 * The Math.round() static method returns the value of a number rounded to the nearest integer. So this seems pretty unstable, but I don't know
 * the reason why it's used, so temporarily I'll add a flag default to true.
 *
 * @param miles number
 * @param round boolean @default true
 * @param n number @default 1
 * @returns null | string
 */
export const milesToKms = (
  miles: number | string | null | undefined,
  round = true,
  n = 1
): string | null => {
  if (!miles) {
    return null
  }
  const zeroes = new Array(n < 1 ? 1 : n).fill(0)
  const milesValue = typeof miles === 'string' ? parseFloat(miles) : miles
  return numeral(
    round
      ? Math.round(milesValue * MILES_KM_CONVERSION_NUMBER)
      : milesValue * MILES_KM_CONVERSION_NUMBER
  ).format(`0.0${zeroes.join('')}`)
}

/**
 *
 * @param kms number
 * @returns null | string
 */
export const kmsToMiles = (
  kms: number | string | null | undefined
): string | null => {
  if (!kms) {
    return null
  }
  return numeral(
    (typeof kms === 'string' ? parseFloat(kms) : kms) /
      MILES_KM_CONVERSION_NUMBER
  ).format('0.0')
}

/**
 *
 * @param kms string | number
 * @returns number
 */
export const pureKmsToMiles = (kms: string | number): number => {
  return (
    (typeof kms === 'string' ? parseFloat(kms) : kms) /
    MILES_KM_CONVERSION_NUMBER
  )
}

export const kmsRateToMilesRate = (
  kmsRate: number | null | undefined
): string => {
  if (!kmsRate) {
    return '0'
  }
  return (kmsRate * MILES_KM_CONVERSION_NUMBER).toFixed(4)
}

export const getUniqueListBy = <T>(
  arr: T[] | null | undefined,
  key: string
): T[] => {
  if (!arr?.length || !key) return []
  return [
    ...new Map(
      arr
        .filter((item) => !!item[key as keyof T])
        .map((item) => [item[key as keyof T], item])
    ).values()
  ]
}

const otherPurposes: string[] = [
  'Medical',
  'Charity',
  'Other',
  'Commute',
  'Moving'
]
const purposeColors: PurposeColors = {
  default: {
    class: 'text-primary',
    hex: '#32B59F'
  },
  other: {
    class: 'text-warning',
    hex: '#E47E30'
  },
  personal: {
    class: 'text-secondary',
    hex: '#4A90E2'
  },
  unclassified: {
    class: 'text-dark',
    hex: '#333333'
  }
}

export const getPurposeColors = (purpose: string): PurposeColor => {
  if (otherPurposes.includes(purpose)) return purposeColors.other
  if (purpose === 'Personal') return purposeColors.personal
  if (['Unclassified', 'All purposes'].includes(purpose))
    return purposeColors.unclassified
  return purposeColors.default
}

export const downloadFile = async (
  url: string,
  filename = '',
  asBlob = false
) => {
  let blobUrl = ''
  if (asBlob) {
    const response = await fetch(url)
    const blob = await response.blob()
    blobUrl = URL.createObjectURL(blob)
  }

  const aEl = document.createElement('a')
  aEl.style.cssText = 'display: none'
  aEl.href = blobUrl || url
  aEl.download = filename
  document.body.appendChild(aEl)
  aEl.click()
  document.body.removeChild(aEl)

  if (blobUrl) {
    URL.revokeObjectURL(blobUrl)
  }
}

export const payPeriodStatusColors: PayPeriodStatusColors = {
  open: { color: '496BD3', bgColor: 'EDF6FF' },
  needs_approval: { color: 'F17200', bgColor: 'FFFBDA' },
  approving: { color: 'F17200', bgColor: 'FFFBDA' },
  scheduled: { color: '00826C', bgColor: 'EAFFFA' },
  marked_as_paid: { color: '00826C', bgColor: 'E2FFED' },
  needs_payment: { color: 'F17200', bgColor: 'FFF1D9' },
  payment_sent: { color: '00826C', bgColor: 'D6FFE6' },
  paying: { color: '00826C', bgColor: 'D6FFE6' },
  partially_paid: { color: 'F17200', bgColor: 'FFF1D9' },
  paid: { color: '00826c', bgColor: 'eafffa' },
  approved: { color: '00826C', bgColor: 'D6FFE6' },
  default: { bgColor: 'eafffa', color: '00826c' }
}

export const complianceStatusColors: ComplianceStatusColors = {
  default: { bgColor: 'EBEBEB', color: '73767D' },
  unsubmitted: { bgColor: 'EBEBEB', color: '73767D' },
  pending_review: { bgColor: 'fff1d9', color: 'f17200' },
  in_review: { bgColor: 'fffbda', color: 'f17200' },
  approved: { bgColor: 'eafffa', color: '00826c' },
  rejected: { bgColor: 'fff1f1', color: 'eb001c' },
  compliant: { bgColor: 'eafffa', color: '00826c' },
  'non-compliant': { bgColor: 'fff1f1', color: 'eb001c' }
}

export const formatAsUSD = (
  value: number,
  minDigits = 2,
  maxDigits = 2
): string => {
  return value.toLocaleString('en-US', {
    style: 'currency',
    currency: 'USD',
    minimumFractionDigits: minDigits,
    maximumFractionDigits: maxDigits
  })
}

export const formatAsCurrency = (
  value: number,
  currency = 'USD',
  locale = 'en-US',
  minDigits = 2,
  maxDigits = 2
): string => {
  return value.toLocaleString(locale, {
    style: 'currency',
    currency,
    minimumFractionDigits: minDigits,
    maximumFractionDigits: maxDigits
  })
}

/**
 * Factor to convert a monthly fixed_rate into its per-period equivalent.
 * Multiply a monthly value by this to get the per-period amount;
 * divide a per-period amount by this to get monthly.
 *
 * BE stores fixed_rate truncated to 4 decimal places. The round-trip
 * (per-period → monthly → BE-truncate → monthly → per-period → formatAsUSD)
 * is stable under formatAsUSD's 2-dp rounding, so we don't pre-round on
 * submit. Reconsider if BE precision ever drops below 4 dp.
 *
 * @param frequency PaymentScheduleConfiguration['frequency']
 * @returns number
 */
export const getMonthlyToPerPeriodMultiplier = (
  frequency?: PaymentScheduleConfiguration['frequency']
): number => {
  switch (frequency) {
    case 'weekly':
      return 12 / 52
    case 'biweekly':
    case 'biweekly_alternate':
      return 12 / 26
    case 'first_and_sixteenth':
      return 12 / 24
    case 'monthly':
    default:
      return 1
  }
}

/**
 * This one work for images that were uploaded with ActiveRecord Ruby on Rails built-in functionality
 * @param path string
 * @returns string (Image Url)
 */
export const getImageUrl = (path: string): string => {
  const apiBaseUrl = (import.meta.env.VITE_APP_API_BASE_URL ?? '').replace(
    /\/v\d\//i,
    ''
  )
  return `${apiBaseUrl}${path}`
}

export const PasswordRulesValidations = {
  required: 'Password is required',
  validate: {
    // Check if password is not just whitespace
    // React Hook Form passes string | boolean, so we need to type guard
    noBlank: (value: string | boolean | undefined) =>
      (typeof value === 'string' && value.trim().length > 0) ||
      ERROR_PASSWORD_NOT_JUST_WHITESPACE,
    // Validate minimum password length (8 characters)
    // Type guard ensures we only check length on string values
    minLength: (value: string | boolean | undefined) =>
      (typeof value === 'string' && value.length >= PASSWORD_MIN_LENGTH) ||
      ERROR_PASSWORD_MIN_LENGTH,
    // Validate maximum password length (20 characters)
    // Safe access to length property with type guard
    maxLength: (value: string | boolean | undefined) =>
      (typeof value === 'string' && value.length <= PASSWORD_MAX_LENGTH) ||
      ERROR_PASSWORD_MAX_LENGTH.format({
        charsTyped: typeof value === 'string' ? value.length : 0
      })
  }
}

export const EmailRulesValidations = {
  required: 'Email is required',
  validate: {
    // Check if email is not just whitespace
    // React Hook Form passes string | boolean, so we need to type guard
    noBlank: (value: string | boolean | undefined) =>
      (typeof value === 'string' && value.trim().length > 0) ||
      ERROR_EMAIL_NOT_JUST_WHITESPACE,
    // Check if email has correct pattern
    // React Hook Form passes string | boolean, so we need to type guard
    pattern: (value: string | boolean | undefined) =>
      (typeof value === 'string' && EMAIL_FORMAT.test(value.trim())) ||
      ERROR_EMAIL_INVALID_FORMAT
  }
}

export const hasInAppUrlSearch = (search: string) => {
  const isAppRegex = new RegExp('[?&]inAppView=([^&]+).*$')
  return isAppRegex.test(search)
}

const purposeToMotusPurpose = {
  Work: MotusPurpose.Business,
  Personal: MotusPurpose.Personal,
  Unclassified: MotusPurpose.Unclassified,
  NeedsReview: MotusPurpose.NeedsReview
}

export const getApiFilterParams = <
  T extends GetTransactionsParams | GetTripsParams
>({
  filters,
  customIncomeSources,
  pageParam,
  isMotus
}: {
  filters: T
  customIncomeSources: string[]
  pageParam: string | undefined
  isMotus?: boolean
}): T => {
  let purposeParam = filters.purpose
  let income_source = null

  if (customIncomeSources.includes(filters.purpose)) {
    purposeParam = 'Work'
    income_source = filters.purpose
  }

  const motusPurpose = isMotus
    ? purposeToMotusPurpose[purposeParam as keyof typeof purposeToMotusPurpose]
    : undefined

  return {
    ...filters,
    purpose: purposeParam,
    income_source: income_source,
    uncategorized: filters.purpose === 'Unclassified',
    starting_after: pageParam ?? null,
    motusPurpose,
    motusHasAnomaly: isMotus ? true : undefined
  }
}

/**
 * Extracts a human-readable message from an Axios error response.
 * @param error ErrorMessage (AxiosError<ErrorPayload>)
 * @param defaultMessage fallback shown when the response has no message/errors
 * @returns string
 */
export const getErrorMessage = (
  error: ErrorMessage,
  defaultMessage?: string
): string => {
  const data = error.response?.data

  if (data?.message) {
    return data.message
  }

  if (Array.isArray(data?.errors) && data.errors.length) {
    return data.errors.join(', ')
  }

  if (typeof data?.errors === 'string') {
    return data.errors
  }

  return (
    defaultMessage ??
    'There was a problem trying to perform this action. Reach out to the eng team.'
  )
}

export const rubyRegexToJs = (rubyPattern: string): RegExp => {
  const jsPattern = rubyPattern
    .replace(/\\A/g, '^')
    .replace(/\\z/g, '$')
    .replace(/\\Z/g, '$')
    .replace(/\{,(\d+)\}/g, '{0,$1}')
  return new RegExp(jsPattern)
}
